/**
 * AppRail — Persistent left navigation rail for Workspace Shell V1
 * 
 * A compact 72px rail with primary workflow navigation:
 * - Search: Find new literature
 * - Screen: Triage digest articles
 * - Learn: Work from LitHub
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  Search,
  Filter,
  GraduationCap,
  Settings,
  Heart,
  Bell,
  User,
  LogOut,
  Menu,
  X,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

interface AppRailProps {
  unscreenedCount?: number;
  libraryCount?: number;
}

interface NavItemProps {
  id: string;
  icon: React.ElementType;
  label: string;
  path: string;
  badge?: number;
  active: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ id, icon: Icon, label, path, badge, active, onClick }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          data-testid={`nav-${id}`}
          variant={active ? 'secondary' : 'ghost'}
          className={`relative w-12 h-12 my-1 justify-center transition-colors ${
            active 
              ? 'bg-[hsl(var(--secondary))] border-l-2 border-[hsl(var(--primary))]' 
              : 'hover:bg-[hsl(var(--muted))]'
          }`}
          onClick={onClick}
          aria-label={label}
        >
          <Icon className="w-5 h-5" aria-hidden />
          {badge !== undefined && badge > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center p-0 text-[10px] bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-0"
              data-testid={`nav-${id}-badge`}
            >
              {badge > 99 ? '99+' : badge}
            </Badge>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right" className="font-medium">
        {label}
        {badge !== undefined && badge > 0 && (
          <span className="ml-2 text-[hsl(var(--muted-foreground))]">
            ({badge})
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Desktop rail component
const DesktopRail: React.FC<AppRailProps & { currentPath: string; navigate: (path: string) => void; logout: () => void }> = ({
  unscreenedCount,
  libraryCount,
  currentPath,
  navigate,
  logout,
}) => {
  const navItems = [
    { id: 'search', icon: Search, label: 'Search', path: '/search' },
    { id: 'screen', icon: Filter, label: 'Screen', path: '/screen', badge: unscreenedCount },
    { id: 'learn', icon: GraduationCap, label: 'Learn', path: '/library/shelf', badge: libraryCount },
    { id: 'digests', icon: BookOpen, label: 'Digests', path: '/digests/manage' },
  ];

  const isActive = (path: string) => {
    if (path === '/search') return currentPath.startsWith('/search');
    if (path === '/screen') return currentPath.startsWith('/screen');
    if (path === '/library/shelf') {
      return currentPath === '/library/shelf' ||
        currentPath.startsWith('/library/digests') ||
        currentPath.startsWith('/library/articles') ||
        currentPath.startsWith('/learn');
    }
    if (path === '/digests/manage') return currentPath.startsWith('/digests');
    return currentPath === path;
  };

  return (
    <aside 
      className="hidden lg:flex h-full w-[72px] flex-col items-center py-3 bg-[hsl(var(--card))] border-r border-[hsl(var(--border))]"
      data-testid="app-rail-desktop"
    >
      {/* Logo */}
      <div 
        className="w-10 h-10 mb-4 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] rounded-lg flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
        onClick={() => navigate('/library')}
        data-testid="nav-logo-button"
      >
        <Heart className="w-5 h-5 text-white fill-white" />
      </div>

      <Separator className="w-8 mb-2" />

      {/* Main navigation */}
      <ScrollArea className="flex-1 w-full">
        <div className="flex flex-col items-center px-2">
          {navItems.map((item) => (
            <NavItem
              key={item.id}
              {...item}
              active={isActive(item.path)}
              onClick={() => navigate(item.path)}
            />
          ))}
        </div>
      </ScrollArea>

      <Separator className="w-8 my-2" />

      {/* Bottom actions */}
      <div className="flex flex-col items-center gap-1 pb-2">
        <NavItem
          id="settings"
          icon={Settings}
          label="Settings"
          path="/preferences"
          active={currentPath === '/preferences'}
          onClick={() => navigate('/preferences')}
        />
        <NavItem
          id="logout"
          icon={LogOut}
          label="Log Out"
          path=""
          active={false}
          onClick={logout}
        />
      </div>
    </aside>
  );
};

// Mobile drawer component
const MobileDrawer: React.FC<AppRailProps & { currentPath: string; navigate: (path: string) => void; logout: () => void; user: any }> = ({
  unscreenedCount,
  libraryCount,
  currentPath,
  navigate,
  logout,
  user,
}) => {
  const [open, setOpen] = React.useState(false);

  const navItems = [
    { id: 'search', icon: Search, label: 'Search', path: '/search', description: 'Find new literature' },
    { id: 'screen', icon: Filter, label: 'Screen', path: '/screen', badge: unscreenedCount, description: 'Triage digest articles' },
    { id: 'learn', icon: GraduationCap, label: 'Learn', path: '/library/shelf', badge: libraryCount, description: 'Work from your LitHub' },
    { id: 'digests', icon: BookOpen, label: 'Digests', path: '/digests/manage', description: 'Manage digest profiles' },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          data-testid="open-rail-drawer-button"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-[hsl(var(--border))]">
            <div className="w-10 h-10 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] rounded-lg flex items-center justify-center">
              <Heart className="w-5 h-5 text-white fill-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-[hsl(var(--foreground))]">LitCurrent</span>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Workspace</p>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 p-3">
            <div className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors text-left ${
                    currentPath.startsWith(item.path)
                      ? 'bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))]'
                      : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
                  }`}
                  data-testid={`drawer-nav-${item.id}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{item.label}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                      {item.description}
                    </div>
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <Badge className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                      {item.badge > 99 ? '99+' : item.badge}
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </ScrollArea>

          {/* User section */}
          <div className="border-t border-[hsl(var(--border))] p-3">
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center">
                <User className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[hsl(var(--foreground))] truncate">
                  {user?.email || 'User'}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => handleNavigate('/preferences')}
              >
                <Settings className="w-4 h-4 mr-1" />
                Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { logout(); setOpen(false); }}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const AppRail: React.FC<AppRailProps> = ({ unscreenedCount = 0, libraryCount = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <DesktopRail
        unscreenedCount={unscreenedCount}
        libraryCount={libraryCount}
        currentPath={location.pathname}
        navigate={navigate}
        logout={handleLogout}
      />
      <MobileDrawer
        unscreenedCount={unscreenedCount}
        libraryCount={libraryCount}
        currentPath={location.pathname}
        navigate={navigate}
        logout={handleLogout}
        user={user}
      />
    </>
  );
};

export default AppRail;
